import type { DcrAddressInput } from '@/core/graphql/generated/types';
import {
	combinePhoneNumber,
	standardizePhoneNumberForSubmission
} from '@/utils/formatters/phoneNumberParts';

import type { usePicklistValues } from '../../_hooks/usePicklistValues';
import type { mapHcpToFormDefaultsIn } from '../mapHcpToFormDefaultsIn';

import { getNetworkFieldValue } from './getNetworkFieldValue';

type FormDefaults = ReturnType<typeof mapHcpToFormDefaultsIn>;
type AddressItem = NonNullable<FormDefaults['addresses']>[number];

type Picklists = ReturnType<typeof usePicklistValues>['picklists'];

type Props = {
	addresses: Partial<AddressItem>[];
	stateOptions: Picklists['stateOptions'];
	countryOptions: Picklists['countryOptions'];
	statusOptions: Picklists['statusOptions'];
	unavailableFields: string[] | undefined;
};

export const mapAddressesToNetworkApi = ({
	addresses,
	stateOptions,
	countryOptions,
	statusOptions,
	unavailableFields
}: Props): DcrAddressInput[] => {
	return addresses?.map(address => {
		const country =
			address?.countryCda_V === 'United Kingdom'
				? 'Great Britain'
				: address?.countryCda_V;
		const resolvedCountry = countryOptions?.find(
			c => c.id === country || c.name === country
		);

		const state = address?.stateCda_V;
		const resolvedState = stateOptions?.find(
			st => st.id === state || st.name === state
		);

		const status = address?.statusCda_V;
		const resolvedStatus = statusOptions?.find(
			s => s.id === status || s.name === status
		);

		const isNew = !address?.vid_V;

		const combinedPhone =
			address?.phoneCda_V === undefined &&
			address?.phoneCountryIso === undefined
				? undefined
				: standardizePhoneNumberForSubmission(
						combinePhoneNumber({
							countryIso: address?.phoneCountryIso ?? '',
							nationalNumber: address?.phoneCda_V ?? ''
						})
					);

		const combinedFax =
			address?.faxCda_V === undefined && address?.faxCountryIso === undefined
				? undefined
				: standardizePhoneNumberForSubmission(
						combinePhoneNumber({
							countryIso: address?.faxCountryIso ?? '',
							nationalNumber: address?.faxCda_V ?? ''
						})
					);

		const mapped: DcrAddressInput = {
			countryCda_VId: resolvedCountry?.id ?? undefined,
			stateCda_VId: resolvedState?.id,
			statusCda_VId: resolvedStatus?.id ?? undefined,
			// cbsa_VId: getNetworkFieldValue(address?.cbsa_V),
			cityCda_V: getNetworkFieldValue(address?.cityCda_V, { isNew }),
			faxCda_V: getNetworkFieldValue(combinedFax, { isNew }),
			phoneCda_V: getNetworkFieldValue(combinedPhone, { isNew }),
			streetAddress1Cda_V: getNetworkFieldValue(address?.streetAddress1Cda_V, {
				isNew
			}),
			streetAddress2Cda_V: getNetworkFieldValue(address?.streetAddress2Cda_V, {
				isNew
			}),
			vid_V: address?.vid_V ? address.vid_V : undefined,
			postalCodeCda_V: getNetworkFieldValue(address?.postalCodeCda_V, {
				isNew
			}),
			postalCodeFullUs_V: getNetworkFieldValue(address?.postalCodeFullUs_V, {
				isNew
			}),
			// Note (Ben): Temporarily removing isPrimaryRelationship_V
			// primaryCda_V:
			// 	address?.primaryCda_V === true || address?.primaryCda_V === false
			// 		? address.primaryCda_V
			// 		: undefined,
			subStateAdminUnit_V: getNetworkFieldValue(address?.subStateAdminUnit_V, {
				isNew
			})
		};

		if (unavailableFields && unavailableFields.length > 0) {
			(Object.keys(mapped) as (keyof DcrAddressInput)[]).forEach(key => {
				if (unavailableFields.includes(key)) {
					delete mapped[key];
				}
			});
		}

		return mapped;
	});
};
